// Cursor damage-circle harvesting. A circle follows the pointer and, on a fixed
// tick (not per click), deals damage to every resource node inside it. Damage
// falls off toward the edge and can crit. When a node's HP hits zero it drops
// its resource and begins a respawn timer. Ore nodes have far more HP, so they
// only crack open once the player's harvest damage has grown.

import type { GameState } from '@core/state';
import { RESOURCES, type ResourceId } from '@data/resources';
import { BALANCE } from '@data/balance';
import { bus } from '@core/events';
import { worldToScreen, camera } from '@render/iso';
import { harvestDamage, harvestRadius, productionMultiplier, progressValue } from './stats';

/** Pointer state in canvas-local pixels, shared with the input wiring in main. */
export const cursor = {
  x: 0,
  y: 0,
  active: false, // true while the pointer is over the play area
};

let tickAccumulator = 0;

function depleteNode(state: GameState, node: GameState['run']['nodes'][number]): void {
  const def = RESOURCES[node.resource];
  node.depleted = true;
  node.hp = 0;
  const { respawnMultMin, respawnMultMax } = BALANCE.harvest;
  node.respawnAt = state.gameTime + def.respawn * (respawnMultMin + Math.random() * (respawnMultMax - respawnMultMin));

  // Manual yield scales with the economy (and any harvest-yield artifact) so
  // harvesting stays worthwhile.
  const amount = def.dropAmount * productionMultiplier(state) * state.run.modifiers.harvestYieldMult;
  state.run.resources[node.resource] += amount;
  state.run.progress += progressValue({ [node.resource]: amount } as Partial<
    Record<ResourceId, number>
  >);

  bus.emit('harvest:drop', {
    gx: node.gx,
    gy: node.gy,
    resource: node.resource,
    amount,
  });
}

function harvestTick(state: GameState): void {
  if (!cursor.active) return;
  // Match the on-screen iso oval: horizontal radius scaled by zoom, vertical half
  // of that. The hit-test is elliptical so it covers exactly what is drawn.
  const rx = harvestRadius(state) * camera.zoom;
  const ry = rx * 0.5;
  const dmg = harvestDamage(state);
  const { critChance, critMult, falloff } = BALANCE.harvest;

  for (const node of state.run.nodes) {
    if (node.depleted) continue;
    const p = worldToScreen(node.gx, node.gy);
    const dx = p.x - cursor.x;
    const dy = p.y - cursor.y;
    // Normalized elliptical distance: t <= 1 means inside the oval.
    const t = Math.sqrt((dx * dx) / (rx * rx) + (dy * dy) / (ry * ry));
    if (t > 1) continue;

    // Edge falloff: full damage at the center, `falloff` fraction at the rim.
    const falloffMult = 1 - (1 - falloff) * t;
    const crit = Math.random() < critChance;
    const dealt = dmg * falloffMult * (crit ? critMult : 1);

    node.hp -= dealt;
    bus.emit('harvest:hit', { gx: node.gx, gy: node.gy, resource: node.resource, crit });

    if (node.hp <= 0) depleteNode(state, node);
  }
}

/** Moves a node to a random free tile in the regrowth ring (keeps it in place
 *  if 40 tries find nothing free). */
function relocateNode(state: GameState, node: GameState['run']['nodes'][number]): void {
  const { respawnRingMin: lo, respawnRingMax: hi } = BALANCE.harvest;
  const taken = new Set(state.run.nodes.filter((n) => n !== node).map((n) => `${n.gx},${n.gy}`));
  for (let attempt = 0; attempt < 40; attempt++) {
    const gx = Math.floor(Math.random() * (2 * hi + 1)) - hi;
    const gy = Math.floor(Math.random() * (2 * hi + 1)) - hi;
    const d = Math.max(Math.abs(gx), Math.abs(gy));
    if (d < lo || d > hi || taken.has(`${gx},${gy}`)) continue;
    node.gx = gx;
    node.gy = gy;
    return;
  }
}

/** Respawns any depleted node whose timer has elapsed. */
function respawnNodes(state: GameState): void {
  for (const node of state.run.nodes) {
    if (node.depleted && state.gameTime >= node.respawnAt) {
      relocateNode(state, node);
      node.depleted = false;
      node.hp = node.maxHp;
      node.respawnAt = 0;
    }
  }
}

export function updateHarvest(state: GameState, dt: number): void {
  respawnNodes(state);

  const interval = 1 / BALANCE.harvest.tickRate;
  tickAccumulator += dt;
  // Cap catch-up ticks so a long frame cannot fire a burst of harvest hits.
  let safety = 0;
  while (tickAccumulator >= interval && safety < 12) {
    harvestTick(state);
    tickAccumulator -= interval;
    safety++;
  }
  if (safety >= 12) tickAccumulator = 0;
}

/** Reset the harvest tick accumulator (called on run restart). */
export function resetHarvest(): void {
  tickAccumulator = 0;
}
